// Strategic internal linking configuration for SEO optimization
// Contains keyword-rich anchor text for consistent internal linking

export interface InternalLinkConfig {
  url: string;
  anchorText: string;
  keywords: string[];
  context: "location" | "airport" | "service" | "route" | "general";
  priority: "high" | "medium" | "low";
  description?: string;
}

// Core service links with keyword-rich anchor text
export const serviceLinks: Record<string, InternalLinkConfig> = {
  taxi_service: {
    url: "/ride",
    anchorText: "taxi service",
    keywords: ["taxi", "taxi service", "transport service"],
    context: "service",
    priority: "high",
    description: "Main taxi service page",
  },
  taxis: {
    url: "/taxi",
    anchorText: "taxis",
    keywords: ["taxis", "taxi services", "local taxis"],
    context: "service",
    priority: "high",
    description: "City taxi services",
  },
  airport_transfers: {
    url: "/airports",
    anchorText: "airport transfers",
    keywords: ["airport transfers", "airport taxi", "airport transport"],
    context: "airport",
    priority: "high",
    description: "Airport transfer services",
  },
  business_transport: {
    url: "/business",
    anchorText: "business transport",
    keywords: [
      "business transport",
      "executive transport",
      "corporate transport",
    ],
    context: "service",
    priority: "medium",
    description: "Business and executive transport",
  },
  coverage_areas: {
    url: "/coverage",
    anchorText: "coverage areas",
    keywords: ["coverage areas", "service areas", "where we serve"],
    context: "general",
    priority: "medium",
    description: "Service coverage areas",
  },
};

// Location-based links with "taxi near me" and city-specific keywords
export const locationLinks: Record<string, InternalLinkConfig> = {
  leicester_taxi: {
    url: "/taxi/leicester",
    anchorText: "Leicester taxi",
    keywords: ["Leicester taxi", "taxi in Leicester", "Leicester transport"],
    context: "location",
    priority: "high",
    description: "Leicester taxi services",
  },
  leicester_taxis: {
    url: "/taxi/leicester",
    anchorText: "Leicester taxis",
    keywords: [
      "Leicester taxis",
      "taxis in Leicester",
      "Leicester taxi service",
    ],
    context: "location",
    priority: "high",
    description: "Leicester taxi services",
  },
  birmingham_taxi: {
    url: "/taxi/birmingham",
    anchorText: "Birmingham taxi",
    keywords: ["Birmingham taxi", "taxi in Birmingham", "Birmingham transport"],
    context: "location",
    priority: "high",
    description: "Birmingham taxi services",
  },
  birmingham_taxis: {
    url: "/taxi/birmingham",
    anchorText: "Birmingham taxis",
    keywords: [
      "Birmingham taxis",
      "taxis in Birmingham",
      "Birmingham taxi service",
    ],
    context: "location",
    priority: "high",
    description: "Birmingham taxi services",
  },
  coventry_taxi: {
    url: "/taxi/coventry",
    anchorText: "Coventry taxi",
    keywords: ["Coventry taxi", "taxi in Coventry", "Coventry transport"],
    context: "location",
    priority: "medium",
    description: "Coventry taxi services",
  },
  coventry_taxis: {
    url: "/taxi/coventry",
    anchorText: "Coventry taxis",
    keywords: ["Coventry taxis", "taxis in Coventry", "Coventry taxi service"],
    context: "location",
    priority: "medium",
    description: "Coventry taxi services",
  },
  derby_taxi: {
    url: "/taxi/derby",
    anchorText: "Derby taxi",
    keywords: ["Derby taxi", "taxi in Derby", "Derby transport"],
    context: "location",
    priority: "medium",
    description: "Derby taxi services",
  },
  nottingham_taxi: {
    url: "/taxi/nottingham",
    anchorText: "Nottingham taxi",
    keywords: ["Nottingham taxi", "taxi in Nottingham", "Nottingham transport"],
    context: "location",
    priority: "medium",
    description: "Nottingham taxi services",
  },
  london_taxi: {
    url: "/taxi/london",
    anchorText: "London taxi",
    keywords: ["London taxi", "taxi in London", "London transport"],
    context: "location",
    priority: "medium",
    description: "London taxi services",
  },

  // Enhanced Leicester area towns and villages
  loughborough_taxi: {
    url: "/taxi/loughborough",
    anchorText: "Loughborough taxi",
    keywords: [
      "Loughborough taxi",
      "taxi in Loughborough",
      "Loughborough transport",
    ],
    context: "location",
    priority: "high",
    description: "Loughborough taxi services",
  },
  coalville_taxi: {
    url: "/taxi/coalville",
    anchorText: "Coalville taxi",
    keywords: ["Coalville taxi", "taxi in Coalville", "Coalville transport"],
    context: "location",
    priority: "medium",
    description: "Coalville taxi services",
  },
  hinckley_taxi: {
    url: "/taxi/hinckley",
    anchorText: "Hinckley taxi",
    keywords: ["Hinckley taxi", "taxi in Hinckley", "Hinckley transport"],
    context: "location",
    priority: "medium",
    description: "Hinckley taxi services",
  },
  market_harborough_taxi: {
    url: "/taxi/market-harborough",
    anchorText: "Market Harborough taxi",
    keywords: [
      "Market Harborough taxi",
      "taxi in Market Harborough",
      "Market Harborough transport",
    ],
    context: "location",
    priority: "medium",
    description: "Market Harborough taxi services",
  },
  melton_mowbray_taxi: {
    url: "/taxi/melton-mowbray",
    anchorText: "Melton Mowbray taxi",
    keywords: [
      "Melton Mowbray taxi",
      "taxi in Melton Mowbray",
      "Melton Mowbray transport",
    ],
    context: "location",
    priority: "medium",
    description: "Melton Mowbray taxi services",
  },
  ashby_de_la_zouch_taxi: {
    url: "/taxi/ashby-de-la-zouch",
    anchorText: "Ashby-de-la-Zouch taxi",
    keywords: [
      "Ashby-de-la-Zouch taxi",
      "taxi in Ashby-de-la-Zouch",
      "Ashby taxi",
    ],
    context: "location",
    priority: "medium",
    description: "Ashby-de-la-Zouch taxi services",
  },
  lutterworth_taxi: {
    url: "/taxi/lutterworth",
    anchorText: "Lutterworth taxi",
    keywords: [
      "Lutterworth taxi",
      "taxi in Lutterworth",
      "Lutterworth transport",
    ],
    context: "location",
    priority: "medium",
    description: "Lutterworth taxi services",
  },
  oadby_taxi: {
    url: "/taxi/oadby",
    anchorText: "Oadby taxi",
    keywords: ["Oadby taxi", "taxi in Oadby", "Oadby transport"],
    context: "location",
    priority: "medium",
    description: "Oadby taxi services",
  },
  wigston_taxi: {
    url: "/taxi/wigston",
    anchorText: "Wigston taxi",
    keywords: ["Wigston taxi", "taxi in Wigston", "Wigston transport"],
    context: "location",
    priority: "medium",
    description: "Wigston taxi services",
  },
  enderby_taxi: {
    url: "/taxi/enderby",
    anchorText: "Enderby taxi",
    keywords: ["Enderby taxi", "taxi in Enderby", "Enderby village taxi"],
    context: "location",
    priority: "medium",
    description: "Enderby village taxi services",
  },
  syston_taxi: {
    url: "/taxi/syston",
    anchorText: "Syston taxi",
    keywords: ["Syston taxi", "taxi in Syston", "Syston transport"],
    context: "location",
    priority: "medium",
    description: "Syston taxi services",
  },
  anstey_taxi: {
    url: "/taxi/anstey",
    anchorText: "Anstey taxi",
    keywords: ["Anstey taxi", "taxi in Anstey", "Anstey village taxi"],
    context: "location",
    priority: "medium",
    description: "Anstey village taxi services",
  },
  groby_taxi: {
    url: "/taxi/groby",
    anchorText: "Groby taxi",
    keywords: ["Groby taxi", "taxi in Groby", "Groby village taxi"],
    context: "location",
    priority: "medium",
    description: "Groby village taxi services",
  },
  kibworth_taxi: {
    url: "/taxi/kibworth",
    anchorText: "Kibworth taxi",
    keywords: ["Kibworth taxi", "taxi in Kibworth", "Kibworth village taxi"],
    context: "location",
    priority: "medium",
    description: "Kibworth village taxi services",
  },

  // Enhanced Birmingham area towns and villages
  solihull_taxi: {
    url: "/taxi/solihull",
    anchorText: "Solihull taxi",
    keywords: ["Solihull taxi", "taxi in Solihull", "Solihull transport"],
    context: "location",
    priority: "high",
    description: "Solihull taxi services",
  },
  sutton_coldfield_taxi: {
    url: "/taxi/sutton-coldfield",
    anchorText: "Sutton Coldfield taxi",
    keywords: [
      "Sutton Coldfield taxi",
      "taxi in Sutton Coldfield",
      "Sutton Coldfield transport",
    ],
    context: "location",
    priority: "high",
    description: "Sutton Coldfield taxi services",
  },
  west_bromwich_taxi: {
    url: "/taxi/west-bromwich",
    anchorText: "West Bromwich taxi",
    keywords: [
      "West Bromwich taxi",
      "taxi in West Bromwich",
      "West Bromwich transport",
    ],
    context: "location",
    priority: "high",
    description: "West Bromwich taxi services",
  },
  dudley_taxi: {
    url: "/taxi/dudley",
    anchorText: "Dudley taxi",
    keywords: ["Dudley taxi", "taxi in Dudley", "Dudley transport"],
    context: "location",
    priority: "high",
    description: "Dudley taxi services",
  },
  walsall_taxi: {
    url: "/taxi/walsall",
    anchorText: "Walsall taxi",
    keywords: ["Walsall taxi", "taxi in Walsall", "Walsall transport"],
    context: "location",
    priority: "high",
    description: "Walsall taxi services",
  },
  wolverhampton_taxi: {
    url: "/taxi/wolverhampton",
    anchorText: "Wolverhampton taxi",
    keywords: [
      "Wolverhampton taxi",
      "taxi in Wolverhampton",
      "Wolverhampton transport",
    ],
    context: "location",
    priority: "high",
    description: "Wolverhampton taxi services",
  },
  smethwick_taxi: {
    url: "/taxi/smethwick",
    anchorText: "Smethwick taxi",
    keywords: ["Smethwick taxi", "taxi in Smethwick", "Smethwick transport"],
    context: "location",
    priority: "high",
    description: "Smethwick taxi services",
  },
  wednesbury_taxi: {
    url: "/taxi/wednesbury",
    anchorText: "Wednesbury taxi",
    keywords: ["Wednesbury taxi", "taxi in Wednesbury", "Wednesbury transport"],
    context: "location",
    priority: "high",
    description: "Wednesbury taxi services",
  },
  tipton_taxi: {
    url: "/taxi/tipton",
    anchorText: "Tipton taxi",
    keywords: ["Tipton taxi", "taxi in Tipton", "Tipton transport"],
    context: "location",
    priority: "high",
    description: "Tipton taxi services",
  },
  bilston_taxi: {
    url: "/taxi/bilston",
    anchorText: "Bilston taxi",
    keywords: ["Bilston taxi", "taxi in Bilston", "Bilston transport"],
    context: "location",
    priority: "high",
    description: "Bilston taxi services",
  },
  willenhall_taxi: {
    url: "/taxi/willenhall",
    anchorText: "Willenhall taxi",
    keywords: ["Willenhall taxi", "taxi in Willenhall", "Willenhall transport"],
    context: "location",
    priority: "high",
    description: "Willenhall taxi services",
  },
  aldridge_taxi: {
    url: "/taxi/aldridge",
    anchorText: "Aldridge taxi",
    keywords: ["Aldridge taxi", "taxi in Aldridge", "Aldridge transport"],
    context: "location",
    priority: "medium",
    description: "Aldridge taxi services",
  },
  tamworth_taxi: {
    url: "/taxi/tamworth",
    anchorText: "Tamworth taxi",
    keywords: ["Tamworth taxi", "taxi in Tamworth", "Tamworth transport"],
    context: "location",
    priority: "high",
    description: "Tamworth taxi services",
  },
  manchester_taxi: {
    url: "/taxi/manchester",
    anchorText: "Manchester taxi",
    keywords: ["Manchester taxi", "taxi in Manchester", "Manchester transport"],
    context: "location",
    priority: "high",
    description: "Manchester taxi services",
  },
  bradford_taxi: {
    url: "/taxis/bradford",
    anchorText: "Bradford taxi",
    keywords: ["Bradford taxi", "taxi in Bradford", "Bradford transport"],
    context: "location",
    priority: "medium",
    description: "Bradford taxi services",
  },
  liverpool_taxi: {
    url: "/taxi/liverpool",
    anchorText: "Liverpool taxi",
    keywords: ["Liverpool taxi", "taxi in Liverpool", "Liverpool transport"],
    context: "location",
    priority: "high",
    description: "Liverpool taxi services",
  },
  huddersfield_taxi: {
    url: "/taxis/huddersfield",
    anchorText: "Huddersfield taxi",
    keywords: [
      "Huddersfield taxi",
      "taxi in Huddersfield",
      "Huddersfield transport",
    ],
    context: "location",
    priority: "medium",
    description: "Huddersfield taxi services",
  },
  halifax_taxi: {
    url: "/taxis/halifax",
    anchorText: "Halifax taxi",
    keywords: ["Halifax taxi", "taxi in Halifax", "Halifax transport"],
    context: "location",
    priority: "medium",
    description: "Halifax taxi services",
  },
  wakefield_taxi: {
    url: "/taxis/wakefield",
    anchorText: "Wakefield taxi",
    keywords: ["Wakefield taxi", "taxi in Wakefield", "Wakefield transport"],
    context: "location",
    priority: "medium",
    description: "Wakefield taxi services",
  },
  birkenhead_taxi: {
    url: "/taxis/birkenhead",
    anchorText: "Birkenhead taxi",
    keywords: ["Birkenhead taxi", "taxi in Birkenhead", "Birkenhead transport"],
    context: "location",
    priority: "medium",
    description: "Birkenhead taxi services",
  },
  southport_taxi: {
    url: "/taxis/southport",
    anchorText: "Southport taxi",
    keywords: ["Southport taxi", "taxi in Southport", "Southport transport"],
    context: "location",
    priority: "medium",
    description: "Southport taxi services",
  },
  st_helens_taxi: {
    url: "/taxis/st-helens",
    anchorText: "St Helens taxi",
    keywords: ["St Helens taxi", "taxi in St Helens", "St Helens transport"],
    context: "location",
    priority: "medium",
    description: "St Helens taxi services",
  },
};

// Airport-specific links with keyword-rich anchor text
export const airportLinks: Record<string, InternalLinkConfig> = {
  heathrow_transfer: {
    url: "/airports/lhr",
    anchorText: "Heathrow airport transfer",
    keywords: [
      "Heathrow transfer",
      "Heathrow airport taxi",
      "Heathrow transport",
    ],
    context: "airport",
    priority: "high",
    description: "Heathrow Airport transfer services",
  },
  gatwick_transfer: {
    url: "/airports/lgw",
    anchorText: "Gatwick airport transfer",
    keywords: ["Gatwick transfer", "Gatwick airport taxi", "Gatwick transport"],
    context: "airport",
    priority: "high",
    description: "Gatwick Airport transfer services",
  },
  manchester_transfer: {
    url: "/airports/man",
    anchorText: "Manchester airport transfer",
    keywords: [
      "Manchester transfer",
      "Manchester airport taxi",
      "Manchester transport",
    ],
    context: "airport",
    priority: "high",
    description: "Manchester Airport transfer services",
  },
  birmingham_airport_transfer: {
    url: "/airports/bhx",
    anchorText: "Birmingham airport transfer",
    keywords: [
      "Birmingham airport transfer",
      "Birmingham airport taxi",
      "BHX transfer",
    ],
    context: "airport",
    priority: "high",
    description: "Birmingham Airport transfer services",
  },
  east_midlands_transfer: {
    url: "/airports/ema",
    anchorText: "East Midlands airport transfer",
    keywords: [
      "East Midlands transfer",
      "East Midlands airport taxi",
      "EMA transfer",
    ],
    context: "airport",
    priority: "high",
    description: "East Midlands Airport transfer services",
  },
  stansted_transfer: {
    url: "/airports/stn",
    anchorText: "Stansted airport transfer",
    keywords: [
      "Stansted transfer",
      "Stansted airport taxi",
      "Stansted transport",
    ],
    context: "airport",
    priority: "medium",
    description: "Stansted Airport transfer services",
  },
  luton_transfer: {
    url: "/airports/ltn",
    anchorText: "Luton airport transfer",
    keywords: ["Luton transfer", "Luton airport taxi", "Luton transport"],
    context: "airport",
    priority: "medium",
    description: "Luton Airport transfer services",
  },
};

// Route-specific links with detailed keyword anchor text
export const routeLinks: Record<string, InternalLinkConfig> = {
  leicester_to_ema: {
    url: "/taxi/leicester",
    anchorText: "Leicester to East Midlands Airport taxi",
    keywords: [
      "Leicester to East Midlands Airport",
      "Leicester EMA taxi",
      "Leicester airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Leicester to East Midlands Airport transfer",
  },
  ema_to_leicester: {
    url: "/airports/ema",
    anchorText: "East Midlands Airport to Leicester taxi",
    keywords: [
      "East Midlands Airport to Leicester",
      "EMA to Leicester taxi",
      "airport to Leicester",
    ],
    context: "route",
    priority: "high",
    description: "East Midlands Airport to Leicester transfer",
  },
  birmingham_to_bhx: {
    url: "/taxi/birmingham",
    anchorText: "Birmingham to Birmingham Airport taxi",
    keywords: [
      "Birmingham to airport",
      "Birmingham BHX taxi",
      "Birmingham airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Birmingham to Birmingham Airport transfer",
  },
  bhx_to_birmingham: {
    url: "/airports/bhx",
    anchorText: "Birmingham Airport to Birmingham taxi",
    keywords: [
      "Birmingham Airport to city",
      "BHX to Birmingham taxi",
      "airport to Birmingham",
    ],
    context: "route",
    priority: "high",
    description: "Birmingham Airport to Birmingham transfer",
  },
  leicester_to_heathrow: {
    url: "/taxi/leicester",
    anchorText: "Leicester to Heathrow Airport taxi",
    keywords: [
      "Leicester to Heathrow",
      "Leicester LHR taxi",
      "Leicester Heathrow transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Leicester to Heathrow Airport transfer",
  },
  heathrow_to_leicester: {
    url: "/airports/lhr",
    anchorText: "Heathrow Airport to Leicester taxi",
    keywords: [
      "Heathrow to Leicester",
      "LHR to Leicester taxi",
      "Heathrow Leicester transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Heathrow Airport to Leicester transfer",
  },
  birmingham_to_heathrow: {
    url: "/taxi/birmingham",
    anchorText: "Birmingham to Heathrow Airport taxi",
    keywords: [
      "Birmingham to Heathrow",
      "Birmingham LHR taxi",
      "Birmingham Heathrow transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Birmingham to Heathrow Airport transfer",
  },
  heathrow_to_birmingham: {
    url: "/airports/lhr",
    anchorText: "Heathrow Airport to Birmingham taxi",
    keywords: [
      "Heathrow to Birmingham",
      "LHR to Birmingham taxi",
      "Heathrow Birmingham transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Heathrow Airport to Birmingham transfer",
  },
  leicester_to_gatwick: {
    url: "/taxi/leicester",
    anchorText: "Leicester to Gatwick Airport taxi",
    keywords: [
      "Leicester to Gatwick",
      "Leicester LGW taxi",
      "Leicester Gatwick transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Leicester to Gatwick Airport transfer",
  },
  gatwick_to_leicester: {
    url: "/airports/lgw",
    anchorText: "Gatwick Airport to Leicester taxi",
    keywords: [
      "Gatwick to Leicester",
      "LGW to Leicester taxi",
      "Gatwick Leicester transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Gatwick Airport to Leicester transfer",
  },
  birmingham_to_manchester: {
    url: "/taxi/birmingham",
    anchorText: "Birmingham to Manchester Airport taxi",
    keywords: [
      "Birmingham to Manchester Airport",
      "Birmingham MAN taxi",
      "Birmingham Manchester transfer",
    ],
    context: "route",
    priority: "low",
    description: "Birmingham to Manchester Airport transfer",
  },
  manchester_to_birmingham: {
    url: "/airports/man",
    anchorText: "Manchester Airport to Birmingham taxi",
    keywords: [
      "Manchester Airport to Birmingham",
      "MAN to Birmingham taxi",
      "Manchester Birmingham transfer",
    ],
    context: "route",
    priority: "low",
    description: "Manchester Airport to Birmingham transfer",
  },
  // Extended UK city and airport route combinations
  coventry_to_bhx: {
    url: "/taxi/coventry",
    anchorText: "Coventry to Birmingham Airport taxi",
    keywords: [
      "Coventry to Birmingham Airport",
      "Coventry BHX taxi",
      "Coventry airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Coventry to Birmingham Airport transfer",
  },
  derby_to_ema: {
    url: "/taxi/derby",
    anchorText: "Derby to East Midlands Airport taxi",
    keywords: [
      "Derby to East Midlands Airport",
      "Derby EMA taxi",
      "Derby airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Derby to East Midlands Airport transfer",
  },
  nottingham_to_ema: {
    url: "/taxi/nottingham",
    anchorText: "Nottingham to East Midlands Airport taxi",
    keywords: [
      "Nottingham to East Midlands Airport",
      "Nottingham EMA taxi",
      "Nottingham airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Nottingham to East Midlands Airport transfer",
  },
  london_to_heathrow: {
    url: "/taxi/london",
    anchorText: "London to Heathrow Airport taxi",
    keywords: [
      "London to Heathrow",
      "London LHR taxi",
      "London Heathrow transfer",
    ],
    context: "route",
    priority: "medium",
    description: "London to Heathrow Airport transfer",
  },
  manchester_to_man: {
    url: "/taxi/manchester",
    anchorText: "Manchester to Manchester Airport taxi",
    keywords: [
      "Manchester to airport",
      "Manchester MAN taxi",
      "Manchester airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Manchester to Manchester Airport transfer",
  },
  leeds_to_lba: {
    url: "/taxi/leeds",
    anchorText: "Leeds to Leeds Bradford Airport taxi",
    keywords: [
      "Leeds to Leeds Bradford Airport",
      "Leeds LBA taxi",
      "Leeds airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Leeds to Leeds Bradford Airport transfer",
  },
  bristol_to_brs: {
    url: "/taxi/bristol",
    anchorText: "Bristol to Bristol Airport taxi",
    keywords: [
      "Bristol to airport",
      "Bristol BRS taxi",
      "Bristol airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Bristol to Bristol Airport transfer",
  },
  // Inter-city routes without airports
  leicester_to_birmingham: {
    url: "/taxi/birmingham",
    anchorText: "Leicester to Birmingham taxi",
    keywords: [
      "Leicester to Birmingham",
      "Leicester Birmingham taxi",
      "intercity taxi service",
    ],
    context: "route",
    priority: "medium",
    description: "Leicester to Birmingham city transfer",
  },
  birmingham_to_leicester: {
    url: "/taxi/leicester",
    anchorText: "Birmingham to Leicester taxi",
    keywords: [
      "Birmingham to Leicester",
      "Birmingham Leicester taxi",
      "intercity taxi service",
    ],
    context: "route",
    priority: "medium",
    description: "Birmingham to Leicester city transfer",
  },
  coventry_to_leicester: {
    url: "/taxi/leicester",
    anchorText: "Coventry to Leicester taxi",
    keywords: [
      "Coventry to Leicester",
      "Coventry Leicester taxi",
      "Midlands taxi service",
    ],
    context: "route",
    priority: "low",
    description: "Coventry to Leicester transfer",
  },

  // Leicester area towns to popular airports
  loughborough_to_ema: {
    url: "/taxi/loughborough",
    anchorText: "Loughborough to East Midlands Airport taxi",
    keywords: [
      "Loughborough to East Midlands Airport",
      "Loughborough EMA taxi",
      "Loughborough airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Loughborough to East Midlands Airport transfer",
  },
  coalville_to_ema: {
    url: "/taxi/coalville",
    anchorText: "Coalville to East Midlands Airport taxi",
    keywords: [
      "Coalville to East Midlands Airport",
      "Coalville EMA taxi",
      "Coalville airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Coalville to East Midlands Airport transfer",
  },
  hinckley_to_bhx: {
    url: "/taxi/hinckley",
    anchorText: "Hinckley to Birmingham Airport taxi",
    keywords: [
      "Hinckley to Birmingham Airport",
      "Hinckley BHX taxi",
      "Hinckley airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Hinckley to Birmingham Airport transfer",
  },
  market_harborough_to_ema: {
    url: "/taxi/market-harborough",
    anchorText: "Market Harborough to East Midlands Airport taxi",
    keywords: [
      "Market Harborough to East Midlands Airport",
      "Market Harborough EMA taxi",
      "Market Harborough airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Market Harborough to East Midlands Airport transfer",
  },
  melton_mowbray_to_ema: {
    url: "/taxi/melton-mowbray",
    anchorText: "Melton Mowbray to East Midlands Airport taxi",
    keywords: [
      "Melton Mowbray to East Midlands Airport",
      "Melton Mowbray EMA taxi",
      "Melton Mowbray airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Melton Mowbray to East Midlands Airport transfer",
  },
  ashby_to_bhx: {
    url: "/taxi/ashby-de-la-zouch",
    anchorText: "Ashby-de-la-Zouch to Birmingham Airport taxi",
    keywords: [
      "Ashby-de-la-Zouch to Birmingham Airport",
      "Ashby BHX taxi",
      "Ashby airport transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Ashby-de-la-Zouch to Birmingham Airport transfer",
  },

  // Birmingham area towns to popular airports
  solihull_to_bhx: {
    url: "/taxi/solihull",
    anchorText: "Solihull to Birmingham Airport taxi",
    keywords: [
      "Solihull to Birmingham Airport",
      "Solihull BHX taxi",
      "Solihull airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Solihull to Birmingham Airport transfer",
  },
  sutton_coldfield_to_bhx: {
    url: "/taxi/sutton-coldfield",
    anchorText: "Sutton Coldfield to Birmingham Airport taxi",
    keywords: [
      "Sutton Coldfield to Birmingham Airport",
      "Sutton Coldfield BHX taxi",
      "Sutton Coldfield airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Sutton Coldfield to Birmingham Airport transfer",
  },
  west_bromwich_to_bhx: {
    url: "/taxi/west-bromwich",
    anchorText: "West Bromwich to Birmingham Airport taxi",
    keywords: [
      "West Bromwich to Birmingham Airport",
      "West Bromwich BHX taxi",
      "West Bromwich airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "West Bromwich to Birmingham Airport transfer",
  },
  dudley_to_bhx: {
    url: "/taxi/dudley",
    anchorText: "Dudley to Birmingham Airport taxi",
    keywords: [
      "Dudley to Birmingham Airport",
      "Dudley BHX taxi",
      "Dudley airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Dudley to Birmingham Airport transfer",
  },
  walsall_to_bhx: {
    url: "/taxi/walsall",
    anchorText: "Walsall to Birmingham Airport taxi",
    keywords: [
      "Walsall to Birmingham Airport",
      "Walsall BHX taxi",
      "Walsall airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Walsall to Birmingham Airport transfer",
  },
  wolverhampton_to_bhx: {
    url: "/taxi/wolverhampton",
    anchorText: "Wolverhampton to Birmingham Airport taxi",
    keywords: [
      "Wolverhampton to Birmingham Airport",
      "Wolverhampton BHX taxi",
      "Wolverhampton airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Wolverhampton to Birmingham Airport transfer",
  },
  smethwick_to_bhx: {
    url: "/taxi/smethwick",
    anchorText: "Smethwick to Birmingham Airport taxi",
    keywords: [
      "Smethwick to Birmingham Airport",
      "Smethwick BHX taxi",
      "Smethwick airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Smethwick to Birmingham Airport transfer",
  },
  tamworth_to_bhx: {
    url: "/taxi/tamworth",
    anchorText: "Tamworth to Birmingham Airport taxi",
    keywords: [
      "Tamworth to Birmingham Airport",
      "Tamworth BHX taxi",
      "Tamworth airport transfer",
    ],
    context: "route",
    priority: "high",
    description: "Tamworth to Birmingham Airport transfer",
  },

  // Popular long-distance routes from towns to major airports
  loughborough_to_heathrow: {
    url: "/taxi/loughborough",
    anchorText: "Loughborough to Heathrow Airport taxi",
    keywords: [
      "Loughborough to Heathrow",
      "Loughborough LHR taxi",
      "Loughborough Heathrow transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Loughborough to Heathrow Airport transfer",
  },
  solihull_to_heathrow: {
    url: "/taxi/solihull",
    anchorText: "Solihull to Heathrow Airport taxi",
    keywords: [
      "Solihull to Heathrow",
      "Solihull LHR taxi",
      "Solihull Heathrow transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Solihull to Heathrow Airport transfer",
  },
  west_bromwich_to_heathrow: {
    url: "/taxi/west-bromwich",
    anchorText: "West Bromwich to Heathrow Airport taxi",
    keywords: [
      "West Bromwich to Heathrow",
      "West Bromwich LHR taxi",
      "West Bromwich Heathrow transfer",
    ],
    context: "route",
    priority: "medium",
    description: "West Bromwich to Heathrow Airport transfer",
  },
  hinckley_to_heathrow: {
    url: "/taxi/hinckley",
    anchorText: "Hinckley to Heathrow Airport taxi",
    keywords: [
      "Hinckley to Heathrow",
      "Hinckley LHR taxi",
      "Hinckley Heathrow transfer",
    ],
    context: "route",
    priority: "low",
    description: "Hinckley to Heathrow Airport transfer",
  },

  // Town to Gatwick routes
  loughborough_to_gatwick: {
    url: "/taxi/loughborough",
    anchorText: "Loughborough to Gatwick Airport taxi",
    keywords: [
      "Loughborough to Gatwick",
      "Loughborough LGW taxi",
      "Loughborough Gatwick transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Loughborough to Gatwick Airport transfer",
  },
  solihull_to_gatwick: {
    url: "/taxi/solihull",
    anchorText: "Solihull to Gatwick Airport taxi",
    keywords: [
      "Solihull to Gatwick",
      "Solihull LGW taxi",
      "Solihull Gatwick transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Solihull to Gatwick Airport transfer",
  },

  // Town to Manchester routes
  loughborough_to_manchester: {
    url: "/taxi/loughborough",
    anchorText: "Loughborough to Manchester Airport taxi",
    keywords: [
      "Loughborough to Manchester Airport",
      "Loughborough MAN taxi",
      "Loughborough Manchester transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Loughborough to Manchester Airport transfer",
  },
  wolverhampton_to_manchester: {
    url: "/taxi/wolverhampton",
    anchorText: "Wolverhampton to Manchester Airport taxi",
    keywords: [
      "Wolverhampton to Manchester Airport",
      "Wolverhampton MAN taxi",
      "Wolverhampton Manchester transfer",
    ],
    context: "route",
    priority: "medium",
    description: "Wolverhampton to Manchester Airport transfer",
  },
};

// "Taxi near me" contextual links
export const nearMeLinks: Record<string, InternalLinkConfig> = {
  taxi_near_me: {
    url: "/taxi",
    anchorText: "taxi near me",
    keywords: ["taxi near me", "local taxi", "nearby taxi"],
    context: "general",
    priority: "high",
    description: "Find taxi services near your location",
  },
  airport_taxi_near_me: {
    url: "/airports",
    anchorText: "airport taxi near me",
    keywords: [
      "airport taxi near me",
      "nearby airport transfer",
      "local airport taxi",
    ],
    context: "airport",
    priority: "high",
    description: "Find airport taxi services near your location",
  },
  // Enhanced local search patterns
  cheap_taxi_near_me: {
    url: "/taxi",
    anchorText: "cheap taxi near me",
    keywords: ["cheap taxi near me", "affordable taxi", "budget taxi service"],
    context: "general",
    priority: "medium",
    description: "Find affordable taxi services in your area",
  },
  reliable_taxi_near_me: {
    url: "/taxi",
    anchorText: "reliable taxi near me",
    keywords: [
      "reliable taxi near me",
      "dependable taxi",
      "trusted taxi service",
    ],
    context: "general",
    priority: "medium",
    description: "Find reliable taxi services near your location",
  },
  local_taxi_service: {
    url: "/taxi",
    anchorText: "local taxi service",
    keywords: ["local taxi service", "neighborhood taxi", "area taxi"],
    context: "general",
    priority: "medium",
    description: "Local taxi services in your area",
  },
};

// Enhanced service variations for better anchor text diversity
export const enhancedServiceLinks: Record<string, InternalLinkConfig> = {
  // Professional service variations
  professional_taxi_service: {
    url: "/ride",
    anchorText: "professional taxi service",
    keywords: [
      "professional taxi",
      "premium taxi service",
      "quality transport",
    ],
    context: "service",
    priority: "high",
    description: "Professional taxi services with experienced drivers",
  },
  reliable_transport_service: {
    url: "/ride",
    anchorText: "reliable transport service",
    keywords: ["reliable transport", "dependable travel", "trusted transport"],
    context: "service",
    priority: "high",
    description: "Reliable transport services across the UK",
  },
  safe_taxi_service: {
    url: "/ride",
    anchorText: "safe taxi service",
    keywords: ["safe taxi", "secure transport", "safety first taxi"],
    context: "service",
    priority: "medium",
    description: "Safe and secure taxi services",
  },

  // Booking and convenience variations
  book_taxi_online: {
    url: "/ride",
    anchorText: "book taxi online",
    keywords: ["book taxi online", "online taxi booking", "taxi reservation"],
    context: "service",
    priority: "high",
    description: "Book your taxi online with instant confirmation",
  },
  instant_taxi_booking: {
    url: "/ride",
    anchorText: "instant taxi booking",
    keywords: ["instant booking", "quick taxi booking", "immediate taxi"],
    context: "service",
    priority: "medium",
    description: "Instant taxi booking service",
  },
  advance_taxi_booking: {
    url: "/ride",
    anchorText: "advance taxi booking",
    keywords: ["advance booking", "pre-book taxi", "schedule taxi"],
    context: "service",
    priority: "medium",
    description: "Book taxis in advance for your journey",
  },

  // Time-based service variations
  "24_7_taxi_service": {
    url: "/ride",
    anchorText: "24/7 taxi service",
    keywords: ["24/7 taxi", "round the clock taxi", "24 hour taxi"],
    context: "service",
    priority: "high",
    description: "24/7 taxi service available anytime",
  },
  night_taxi_service: {
    url: "/ride",
    anchorText: "night taxi service",
    keywords: ["night taxi", "late night transport", "midnight taxi"],
    context: "service",
    priority: "medium",
    description: "Night taxi services for late travel",
  },
  early_morning_taxi: {
    url: "/ride",
    anchorText: "early morning taxi",
    keywords: ["early morning taxi", "dawn pickup", "early taxi"],
    context: "service",
    priority: "medium",
    description: "Early morning taxi services for early departures",
  },

  // Pricing and value variations
  fixed_price_taxi: {
    url: "/ride",
    anchorText: "fixed price taxi",
    keywords: ["fixed price taxi", "no surge pricing", "transparent pricing"],
    context: "service",
    priority: "high",
    description: "Fixed price taxi services with no surge pricing",
  },
  affordable_taxi_service: {
    url: "/ride",
    anchorText: "affordable taxi service",
    keywords: ["affordable taxi", "budget transport", "cheap taxi"],
    context: "service",
    priority: "medium",
    description: "Affordable taxi services at competitive rates",
  },
  premium_taxi_service: {
    url: "/business",
    anchorText: "premium taxi service",
    keywords: ["premium taxi", "luxury transport", "executive service"],
    context: "service",
    priority: "medium",
    description: "Premium taxi services for special occasions",
  },
};

// Enhanced airport service variations
export const enhancedAirportLinks: Record<string, InternalLinkConfig> = {
  // Service type variations
  airport_pickup_service: {
    url: "/airports",
    anchorText: "airport pickup service",
    keywords: ["airport pickup", "arrival pickup", "airport collection"],
    context: "airport",
    priority: "high",
    description: "Professional airport pickup services",
  },
  airport_drop_off_service: {
    url: "/airports",
    anchorText: "airport drop off service",
    keywords: ["airport drop off", "departure service", "airport delivery"],
    context: "airport",
    priority: "high",
    description: "Reliable airport drop off services",
  },
  flight_tracking_service: {
    url: "/airports",
    anchorText: "flight tracking taxi service",
    keywords: ["flight tracking", "monitored pickup", "flight delay service"],
    context: "airport",
    priority: "medium",
    description: "Taxi service with flight tracking for delayed flights",
  },

  // Timing and convenience
  early_flight_taxi: {
    url: "/airports",
    anchorText: "early flight taxi",
    keywords: ["early flight taxi", "dawn airport transfer", "early departure"],
    context: "airport",
    priority: "medium",
    description: "Early morning airport transfers for dawn flights",
  },
  late_flight_pickup: {
    url: "/airports",
    anchorText: "late flight pickup",
    keywords: [
      "late flight pickup",
      "midnight arrival",
      "night airport transfer",
    ],
    context: "airport",
    priority: "medium",
    description: "Late night airport pickup services",
  },
  meet_and_greet_service: {
    url: "/airports",
    anchorText: "airport meet and greet",
    keywords: [
      "meet and greet",
      "personal pickup",
      "assisted airport transfer",
    ],
    context: "airport",
    priority: "medium",
    description: "Airport meet and greet services",
  },

  // Business and group variations
  corporate_airport_transfer: {
    url: "/business",
    anchorText: "corporate airport transfer",
    keywords: [
      "corporate transfer",
      "business airport service",
      "executive airport",
    ],
    context: "airport",
    priority: "medium",
    description: "Corporate airport transfer services",
  },
  group_airport_transfer: {
    url: "/airports",
    anchorText: "group airport transfer",
    keywords: ["group transfer", "multiple passenger", "large group transport"],
    context: "airport",
    priority: "medium",
    description: "Group airport transfer services for multiple passengers",
  },
};

// Combined configuration for easy access
const baseInternalLinks = {
  ...serviceLinks,
  ...locationLinks,
  ...airportLinks,
  ...routeLinks,
  ...nearMeLinks,
  ...enhancedServiceLinks,
  ...enhancedAirportLinks,
};

// Add dynamically generated location anchor text
const dynamicLocationLinks = generateAllLocationAnchorText();

// Export the complete combined configuration
export const allInternalLinks = {
  ...baseInternalLinks,
  ...dynamicLocationLinks,
};

// Helper functions for getting specific types of links
export const getLinksByContext = (
  context: InternalLinkConfig["context"],
): InternalLinkConfig[] => {
  return Object.values(allInternalLinks).filter(
    (link) => link.context === context,
  );
};

export const getLinksByPriority = (
  priority: InternalLinkConfig["priority"],
): InternalLinkConfig[] => {
  return Object.values(allInternalLinks).filter(
    (link) => link.priority === priority,
  );
};

export const getHighPriorityLinks = (): InternalLinkConfig[] => {
  return getLinksByPriority("high");
};

export const getLocationSpecificLinks = (
  cityName: string,
): InternalLinkConfig[] => {
  const cityLower = cityName.toLowerCase();
  return Object.values(allInternalLinks).filter(
    (link) =>
      link.keywords.some((keyword) =>
        keyword.toLowerCase().includes(cityLower),
      ) || link.url.includes(`/taxi/${cityLower}`),
  );
};

export const getAirportSpecificLinks = (
  airportCode: string,
): InternalLinkConfig[] => {
  const codeLower = airportCode.toLowerCase();
  return Object.values(allInternalLinks).filter(
    (link) =>
      link.keywords.some((keyword) =>
        keyword.toLowerCase().includes(codeLower),
      ) || link.url.includes(`/airports/${codeLower}`),
  );
};

export const getRouteSpecificLinks = (
  fromLocation: string,
  toLocation: string,
): InternalLinkConfig[] => {
  const from = fromLocation.toLowerCase();
  const to = toLocation.toLowerCase();

  return Object.values(allInternalLinks).filter(
    (link) =>
      link.context === "route" &&
      ((link.keywords.some((keyword) => keyword.toLowerCase().includes(from)) &&
        link.keywords.some((keyword) => keyword.toLowerCase().includes(to))) ||
        (link.anchorText.toLowerCase().includes(from) &&
          link.anchorText.toLowerCase().includes(to))),
  );
};

// Enhanced function to get contextual links with better anchor text variety
export const getContextualLinks = (
  pageType: string,
  location?: string,
  airportCode?: string,
): InternalLinkConfig[] => {
  let links: InternalLinkConfig[] = [];

  const coreServiceLinks: InternalLinkConfig[] = [
    {
      url: "/ride",
      anchorText: "book taxi online",
      keywords: ["ride", "book taxi", "taxi service"],
      context: "service",
      priority: "high",
      description: "Main booking page for rides",
    },
    {
      url: "/safety",
      anchorText: "safety & support",
      keywords: ["safety", "support", "help"],
      context: "service",
      priority: "high",
      description: "Safety information and support",
    },
    {
      url: "/contact",
      anchorText: "contact Tigola",
      keywords: ["contact", "support", "customer service"],
      context: "service",
      priority: "high",
      description: "Contact page",
    },
  ];

  switch (pageType) {
    case "homepage":
      // Mix of high priority service links and near me patterns
      links = [
        ...getHighPriorityLinks().slice(0, 4),
        ...Object.values(allInternalLinks)
          .filter(
            (link) =>
              link.context === "general" && link.anchorText.includes("near me"),
          )
          .slice(0, 2),
        ...Object.values(allInternalLinks)
          .filter(
            (link) =>
              link.anchorText.includes("professional") ||
              link.anchorText.includes("reliable"),
          )
          .slice(0, 2),
      ];
      break;

    case "city":
    case "town":
    case "village":
    case "area":
      if (location) {
        // Location-specific + nearby airports + service variants + popular routes
        const locationLinks = getLocationSpecificLinks(location).slice(0, 4);
        const nearbyAirportLinks = getLinksByContext("airport").slice(0, 3);
        const popularRoutes = getRouteSpecificLinks(location, "airport").slice(
          0,
          2,
        );
        const serviceVariants = Object.values(allInternalLinks)
          .filter(
            (link) =>
              (link.anchorText.includes("book") ||
                link.anchorText.includes("24/7") ||
                link.anchorText.includes("reliable")) &&
              link.context === "service",
          )
          .slice(0, 3);

        links = [
          ...coreServiceLinks,
          ...locationLinks,
          ...nearbyAirportLinks,
          ...popularRoutes,
          ...serviceVariants,
        ];
      }
      break;

    case "airport":
      if (airportCode) {
        // Airport-specific + nearby cities + towns + airport service variants
        const airportLinks = getAirportSpecificLinks(airportCode).slice(0, 4);
        const nearbyCityLinks = getLinksByContext("location").slice(0, 3);
        const nearbyTownRoutes = Object.values(allInternalLinks)
          .filter(
            (link) =>
              link.context === "route" &&
              link.keywords.some((keyword) =>
                keyword.toLowerCase().includes(airportCode.toLowerCase()),
              ),
          )
          .slice(0, 2);
        const airportServiceVariants = Object.values(allInternalLinks)
          .filter(
            (link) =>
              (link.anchorText.includes("pickup") ||
                link.anchorText.includes("flight") ||
                link.anchorText.includes("early")) &&
              link.context === "airport",
          )
          .slice(0, 3);

        links = [
          ...coreServiceLinks,
          ...airportLinks,
          ...nearbyCityLinks,
          ...nearbyTownRoutes,
          ...airportServiceVariants,
        ];
      }
      break;

    case "service":
      // Mix of locations, airports, towns, and service variations
      const topCities = getLinksByContext("location").slice(0, 3);
      const topAirports = getLinksByContext("airport").slice(0, 3);
      const popularTownRoutes = Object.values(allInternalLinks)
        .filter((link) => link.context === "route")
        .slice(0, 2);
      const serviceVariations = Object.values(allInternalLinks)
        .filter(
          (link) =>
            link.context === "service" &&
            (link.anchorText.includes("professional") ||
              link.anchorText.includes("safe") ||
              link.anchorText.includes("fixed")),
        )
        .slice(0, 4);

      links = [
        ...coreServiceLinks,
        ...topCities,
        ...topAirports,
        ...popularTownRoutes,
        ...serviceVariations,
      ];
      break;

    case "business":
      // Business-focused links including major towns
      const businessServiceLinks = Object.values(allInternalLinks)
        .filter(
          (link) =>
            link.anchorText.includes("business") ||
            link.anchorText.includes("executive") ||
            link.anchorText.includes("corporate") ||
            link.anchorText.includes("premium"),
        )
        .slice(0, 4);
      const topBusinessCities = [
        "London",
        "Birmingham",
        "Manchester",
        "Solihull",
        "Wolverhampton",
      ]
        .map((city) =>
          Object.values(allInternalLinks).find((link) =>
            link.keywords.some((keyword) =>
              keyword.toLowerCase().includes(city.toLowerCase()),
            ),
          ),
        )
        .filter(Boolean)
        .slice(0, 3);
      const airportBusinessLinks = Object.values(allInternalLinks)
        .filter(
          (link) => link.context === "airport" && link.priority === "high",
        )
        .slice(0, 2);

      links = [
        ...businessServiceLinks,
        ...topBusinessCities,
        ...airportBusinessLinks,
      ];
      break;

    default:
      // Default mix with variety including towns and villages
      links = [
        ...coreServiceLinks,
        ...getLinksByPriority("high").slice(0, 3),
        ...getLinksByPriority("medium").slice(0, 3),
        ...Object.values(allInternalLinks)
          .filter((link) => link.anchorText.includes("near me"))
          .slice(0, 2),
      ];
  }

  // Remove duplicates and ensure variety in anchor text
  const uniqueLinks = links.filter(
    (link, index, self) =>
      index ===
      self.findIndex(
        (l) => l.url === link.url && l.anchorText === link.anchorText,
      ),
  );

  // Limit to 8-12 links to ensure comprehensive coverage while avoiding over-optimization
  return uniqueLinks.slice(0, 12);
};

// Function to get anchor text variations for a specific keyword
export const getAnchorTextVariations = (baseKeyword: string): string[] => {
  const variations: Record<string, string[]> = {
    taxi: [
      "taxi service",
      "taxi near me",
      "book taxi online",
      "reliable taxi service",
      "professional taxi",
      "local taxi service",
      "24/7 taxi service",
    ],
    airport: [
      "airport transfers",
      "airport taxi",
      "airport pickup service",
      "airport transport",
      "flight tracking service",
      "professional airport transfer",
      "reliable airport service",
    ],
    transfer: [
      "airport transfers",
      "transfer service",
      "pickup service",
      "transport service",
      "professional transfers",
      "reliable transfers",
    ],
  };

  return variations[baseKeyword.toLowerCase()] || [baseKeyword];
};

// Function to get SEO-optimized anchor text for dynamic content
export const getSEOAnchorText = (
  linkType:
    | "city"
    | "town"
    | "village"
    | "area"
    | "airport"
    | "route"
    | "service",
  primaryKeyword: string,
  secondaryKeyword?: string,
  context?: string,
): string => {
  const templates: Record<string, string[]> = {
    city: [
      `${primaryKeyword} taxi`,
      `${primaryKeyword} taxis`,
      `taxi in ${primaryKeyword}`,
      `${primaryKeyword} transport service`,
      `professional taxi ${primaryKeyword}`,
      `reliable ${primaryKeyword} taxis`,
    ],
    town: [
      `${primaryKeyword} taxi`,
      `${primaryKeyword} taxis`,
      `taxi in ${primaryKeyword}`,
      `${primaryKeyword} transport service`,
      `${primaryKeyword} airport transfer`,
      `reliable ${primaryKeyword} taxi service`,
    ],
    village: [
      `${primaryKeyword} taxi`,
      `${primaryKeyword} village taxi`,
      `taxi in ${primaryKeyword}`,
      `${primaryKeyword} transport`,
      `${primaryKeyword} airport transfer`,
      `local ${primaryKeyword} taxi service`,
    ],
    area: [
      `${primaryKeyword} taxi`,
      `${primaryKeyword} area taxis`,
      `taxi in ${primaryKeyword}`,
      `${primaryKeyword} transport service`,
      `${primaryKeyword} airport transfer`,
      `${primaryKeyword} local taxi`,
    ],
    airport: [
      `${primaryKeyword} airport transfer`,
      `${primaryKeyword} airport taxi`,
      `${primaryKeyword} transfer service`,
      `${primaryKeyword} airport transport`,
      `professional ${primaryKeyword} transfer`,
      `reliable ${primaryKeyword} airport service`,
    ],
    route: [
      `${primaryKeyword} to ${secondaryKeyword} taxi`,
      `${primaryKeyword} to ${secondaryKeyword} transfer`,
      `${primaryKeyword} ${secondaryKeyword} service`,
      `transport from ${primaryKeyword} to ${secondaryKeyword}`,
      `${primaryKeyword} ${secondaryKeyword} route`,
      `${primaryKeyword} to ${secondaryKeyword} airport transfer`,
    ],
    service: [
      `${primaryKeyword} service`,
      `professional ${primaryKeyword}`,
      `reliable ${primaryKeyword}`,
      `book ${primaryKeyword} online`,
      `24/7 ${primaryKeyword}`,
      `${primaryKeyword} near me`,
    ],
  };

  const templateList = templates[linkType] || [`${primaryKeyword} service`];

  // Use context to select most appropriate template
  if (context === "local") {
    return (
      templateList.find((t) => t.includes("near me") || t.includes("local")) ||
      templateList[0]
    );
  } else if (context === "professional") {
    return (
      templateList.find(
        (t) => t.includes("professional") || t.includes("reliable"),
      ) || templateList[0]
    );
  } else if (context === "airport") {
    return templateList.find((t) => t.includes("airport")) || templateList[0];
  }

  // Default to first template
  return templateList[0];
};

// Generate comprehensive anchor text for all location types with airport routes
export function generateLocationAnchorText(
  locationName: string,
  locationType: "city" | "town" | "village" | "area",
  nearestAirport?: string,
): { [key: string]: InternalLinkConfig } {
  const slug = locationName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const baseUrl = `/taxi/${slug}`;

  const baseLinks: { [key: string]: InternalLinkConfig } = {
    [`${slug}_taxi`]: {
      url: baseUrl,
      anchorText: `${locationName} taxi`,
      keywords: [
        `${locationName} taxi`,
        `taxi in ${locationName}`,
        `${locationName} transport`,
      ],
      context: "location",
      priority:
        locationType === "city"
          ? "high"
          : locationType === "town"
            ? "medium"
            : "low",
      description: `${locationName} ${locationType} taxi services`,
    },
    [`${slug}_taxis`]: {
      url: baseUrl,
      anchorText: `${locationName} taxis`,
      keywords: [
        `${locationName} taxis`,
        `taxis in ${locationName}`,
        `${locationName} taxi service`,
      ],
      context: "location",
      priority:
        locationType === "city"
          ? "high"
          : locationType === "town"
            ? "medium"
            : "low",
      description: `${locationName} ${locationType} taxi services`,
    },
  };

  // Add airport route if nearest airport is provided
  if (nearestAirport) {
    const airportSlug = nearestAirport
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    baseLinks[`${slug}_to_${airportSlug}`] = {
      url: baseUrl,
      anchorText: `${locationName} to ${nearestAirport} taxi`,
      keywords: [
        `${locationName} to ${nearestAirport}`,
        `${locationName} ${nearestAirport} taxi`,
        `${locationName} airport transfer`,
      ],
      context: "route",
      priority: "high",
      description: `${locationName} to ${nearestAirport} transfer`,
    };
  }

  return baseLinks;
}

// Export comprehensive location anchor text data
export function generateAllLocationAnchorText(): {
  [key: string]: InternalLinkConfig;
} {
  const locationData = [
    // Leicester area locations
    {
      name: "Loughborough",
      type: "town" as const,
      airport: "East Midlands Airport",
    },
    {
      name: "Coalville",
      type: "town" as const,
      airport: "East Midlands Airport",
    },
    { name: "Hinckley", type: "town" as const, airport: "Birmingham Airport" },
    {
      name: "Market Harborough",
      type: "town" as const,
      airport: "East Midlands Airport",
    },
    {
      name: "Melton Mowbray",
      type: "town" as const,
      airport: "East Midlands Airport",
    },
    {
      name: "Ashby-de-la-Zouch",
      type: "town" as const,
      airport: "Birmingham Airport",
    },
    {
      name: "Lutterworth",
      type: "town" as const,
      airport: "Birmingham Airport",
    },
    { name: "Oadby", type: "town" as const, airport: "East Midlands Airport" },
    {
      name: "Wigston",
      type: "town" as const,
      airport: "East Midlands Airport",
    },
    {
      name: "Enderby",
      type: "village" as const,
      airport: "East Midlands Airport",
    },
    { name: "Syston", type: "town" as const, airport: "East Midlands Airport" },
    {
      name: "Anstey",
      type: "village" as const,
      airport: "East Midlands Airport",
    },
    {
      name: "Groby",
      type: "village" as const,
      airport: "East Midlands Airport",
    },
    {
      name: "Kibworth",
      type: "village" as const,
      airport: "East Midlands Airport",
    },

    // Birmingham area locations
    { name: "Solihull", type: "town" as const, airport: "Birmingham Airport" },
    {
      name: "Sutton Coldfield",
      type: "town" as const,
      airport: "Birmingham Airport",
    },
    {
      name: "West Bromwich",
      type: "town" as const,
      airport: "Birmingham Airport",
    },
    { name: "Dudley", type: "town" as const, airport: "Birmingham Airport" },
    { name: "Walsall", type: "town" as const, airport: "Birmingham Airport" },
    {
      name: "Wolverhampton",
      type: "city" as const,
      airport: "Birmingham Airport",
    },
    { name: "Smethwick", type: "town" as const, airport: "Birmingham Airport" },
    {
      name: "Wednesbury",
      type: "town" as const,
      airport: "Birmingham Airport",
    },
    { name: "Tipton", type: "town" as const, airport: "Birmingham Airport" },
    { name: "Bilston", type: "town" as const, airport: "Birmingham Airport" },
    {
      name: "Willenhall",
      type: "town" as const,
      airport: "Birmingham Airport",
    },
    { name: "Aldridge", type: "town" as const, airport: "Birmingham Airport" },
    { name: "Tamworth", type: "town" as const, airport: "Birmingham Airport" },

    // High-priority missing towns
    {
      name: "Bradford",
      type: "city" as const,
      airport: "Leeds Bradford Airport",
    },
    {
      name: "Huddersfield",
      type: "town" as const,
      airport: "Leeds Bradford Airport",
    },
    {
      name: "Halifax",
      type: "town" as const,
      airport: "Leeds Bradford Airport",
    },
    {
      name: "Wakefield",
      type: "city" as const,
      airport: "Leeds Bradford Airport",
    },
    {
      name: "Birkenhead",
      type: "town" as const,
      airport: "Liverpool John Lennon Airport",
    },
    {
      name: "Bootle",
      type: "town" as const,
      airport: "Liverpool John Lennon Airport",
    },
    {
      name: "Wallasey",
      type: "town" as const,
      airport: "Liverpool John Lennon Airport",
    },
    {
      name: "Southport",
      type: "town" as const,
      airport: "Liverpool John Lennon Airport",
    },
    {
      name: "St Helens",
      type: "town" as const,
      airport: "Liverpool John Lennon Airport",
    },
    { name: "Rotherham", type: "town" as const, airport: "Manchester Airport" },
    {
      name: "Doncaster",
      type: "town" as const,
      airport: "Leeds Bradford Airport",
    },
    { name: "Barnsley", type: "town" as const, airport: "Manchester Airport" },
    {
      name: "Sunderland",
      type: "city" as const,
      airport: "Newcastle International Airport",
    },
    {
      name: "Gateshead",
      type: "town" as const,
      airport: "Newcastle International Airport",
    },
    { name: "Trafford", type: "area" as const, airport: "Manchester Airport" },
    { name: "Wigan", type: "town" as const, airport: "Manchester Airport" },
    { name: "Tameside", type: "area" as const, airport: "Manchester Airport" },
  ];

  let allAnchorText: { [key: string]: InternalLinkConfig } = {};

  locationData.forEach((location) => {
    const locationLinks = generateLocationAnchorText(
      location.name,
      location.type,
      location.airport,
    );
    allAnchorText = { ...allAnchorText, ...locationLinks };
  });

  return allAnchorText;
}

// Enhanced function specifically for getting links for town/village/area pages
export const getLocationPageLinks = (
  locationType: "city" | "town" | "village" | "area",
  locationName: string,
): InternalLinkConfig[] => {
  const locationSpecific = getLocationSpecificLinks(locationName);
  const popularAirportRoutes = getPopularAirportRoutes(locationName);
  const nearbyAirports = getLinksByContext("airport").slice(0, 2);
  const professionalServices = Object.values(allInternalLinks)
    .filter(
      (link) =>
        link.context === "service" &&
        (link.anchorText.includes("professional") ||
          link.anchorText.includes("reliable") ||
          link.anchorText.includes("24/7")),
    )
    .slice(0, 3);

  const allLinks = [
    ...locationSpecific,
    ...popularAirportRoutes,
    ...nearbyAirports,
    ...professionalServices,
  ];

  // Remove duplicates
  const uniqueLinks = allLinks.filter(
    (link, index, self) =>
      index ===
      self.findIndex(
        (l) => l.url === link.url && l.anchorText === link.anchorText,
      ),
  );

  return uniqueLinks.slice(0, 10);
};

// Get all location links by type (for towns, villages, areas)
export const getLocationLinksByType = (
  locationType: "city" | "town" | "village" | "area",
): InternalLinkConfig[] => {
  return Object.values(allInternalLinks).filter(
    (link) =>
      link.context === "location" &&
      (link.description?.toLowerCase().includes(locationType) ||
        link.anchorText.toLowerCase().includes(locationType)),
  );
};

// Get popular airport routes for any location
export const getPopularAirportRoutes = (
  locationName: string,
): InternalLinkConfig[] => {
  const location = locationName.toLowerCase();
  return Object.values(allInternalLinks).filter(
    (link) =>
      link.context === "route" &&
      link.keywords.some(
        (keyword) =>
          keyword.toLowerCase().includes(location) &&
          (keyword.toLowerCase().includes("airport") ||
            keyword.toLowerCase().includes("lhr") ||
            keyword.toLowerCase().includes("lgw") ||
            keyword.toLowerCase().includes("bhx") ||
            keyword.toLowerCase().includes("ema") ||
            keyword.toLowerCase().includes("man")),
      ),
  );
};
